<script lang="ts">
	import LoadingIndicator from '@astlai/common/LoadingIndicator.svelte';
	import Markdown from '$lib/Markdown.svelte';
	import type { CitationAttrs } from '$lib/markdown/types';
	import RoleGate from '@astlai/common/RoleGate.svelte';
	import { findErrorMessage } from '@astlai/common/util';
	import { getChatTimeStamp } from '@astlai/common/util/time';
	import { page } from '$app/state';
	import { getChatContext } from 'src/contexts/chat.context.svelte';
	import { citationHash } from 'src/contexts/citation-registry.context.svelte';
	import { getDocumentContext } from 'src/contexts/documents.context.svelte';
	import { getTenantContext } from 'src/contexts/tenant.context.svelte';
	import { getModalContext } from 'src/contexts/modal.context.svelte';
	import type { ChatMessage } from 'src/types/llm';
	import { onMount } from 'svelte';
	import CloseIcon from 'virtual:icons/material-symbols/close-rounded';
	import InfoIcon from 'virtual:icons/material-symbols/info-outline';
	import StopIcon from 'virtual:icons/material-symbols/stop-rounded';
	import ChatFooter from './ChatFooter.svelte';
	import ReasoningBlock from './ReasoningBlock.svelte';
	import RetryIcon from 'virtual:icons/material-symbols/repeat-rounded';
	import MessageInfoDialog from './dialogs/MessageInfoDialog.svelte';
	import { t } from './i18n/tranlsator.svelte';
	import { tooltip } from './tooltip/tootlip.svelte';

	const chatCtx = getChatContext();
	const docCtx = getDocumentContext();
	const tenantCtx = getTenantContext();

	const hideThinking = $derived(
		(page.data.tenant ?? tenantCtx?.tenant)?.feature_flags?.queryClassification?.settings
			?.defaultClassification?.hide_thinking
	);
	const modalCtx = getModalContext();
	let containerEl: HTMLElement = $state(null);
	let mutationTrigger = $state(0);
	let userScrolledManually = $state(false);
	let autoScrollTimeout: ReturnType<typeof setTimeout>;

	// Reset scroll lock when user sends a new message
	$effect(() => {
		const messages = chatCtx.presentableMessages;
		if (messages?.at(-1)?.role === 'user') {
			userScrolledManually = false;
		}
	});

	const chatSpacerHeight = $derived.by(() => {
		void mutationTrigger; // depend on it
		void chatCtx.conversation.id;

		if (!containerEl) {
			return 0;
		}

		const messagesContainer = containerEl.firstElementChild;
		const children = [...(messagesContainer?.children ?? [])] as HTMLElement[];
		const count = chatCtx.isReasoning ? 3 : 2;
		const combinedHeight = children
			.slice(-(count + 1), -1)
			.reduce((sum, el) => sum + el.clientHeight, 0);
		const gap = 16 * 3 + 8; // gap spacing and headroom for

		const finalHeight = Math.max(containerEl.clientHeight - combinedHeight - gap, 0);
		return `${finalHeight}px`;
	});

	function scrollToBottom() {
		if (!containerEl) {
			return;
		}

		const messagesContainer = containerEl.firstElementChild;

		const lastChild = messagesContainer.lastElementChild as HTMLElement;
		const secondToLast = lastChild?.previousElementSibling as HTMLElement;
		const thirdToLast = secondToLast?.previousElementSibling as HTMLElement;

		const combinedHeight =
			(lastChild?.clientHeight || 0) +
			(secondToLast?.clientHeight || 0) +
			(thirdToLast?.clientHeight || 0);

		const gap = 16 * 3; // gap spacing and headroom

		clearTimeout(autoScrollTimeout);
		autoScrollTimeout = setTimeout(() => {
			autoScrollTimeout = undefined;
		}, 500);

		containerEl?.scrollTo({
			top: messagesContainer.clientHeight - combinedHeight - gap || 0,
			behavior: 'smooth'
		});
	}

	onMount(() => {
		const scrollHandler = () => {
			if (autoScrollTimeout) {
				return;
			}
			const distanceFromBottom =
				containerEl.scrollHeight - containerEl.scrollTop - containerEl.clientHeight;
			userScrolledManually = distanceFromBottom > 100;
		};
		containerEl.addEventListener('scroll', scrollHandler);

		const resizeHandler = () => {
			mutationTrigger++;
			if (!userScrolledManually) {
				scrollToBottom();
			}
		};

		window.visualViewport.addEventListener('resize', resizeHandler);

		const mutationObserver = new MutationObserver(() => {
			mutationTrigger++;
			if (chatCtx.latestAssistantMessage?.status !== 'in_progress' && !userScrolledManually) {
				scrollToBottom();
			}
		});

		mutationObserver.observe(containerEl.firstElementChild, {
			childList: true,
			subtree: true
		});

		return () => {
			containerEl.removeEventListener('scroll', scrollHandler);
			mutationObserver.disconnect();
			window.removeEventListener('resize', resizeHandler);
		};
	});

	function openMessageInfoDialog(message: ChatMessage) {
		modalCtx.openModal(MessageInfoDialog, { message });
	}

	function handleRefClick(attrs: CitationAttrs) {
		docCtx.selectByHash(attrs.hash);
	}
</script>

<div class="chat-dialog">
	<div bind:this={containerEl} class="messages-container">
		<div class="messages-list">
			{#each chatCtx.presentableMessages as message, i (message.id)}
				<div class="message {message.role}">
					{#if message.role === 'assistant' && message.reasoning_content && !hideThinking}
						<ReasoningBlock
							reasoningContent={message.reasoning_content}
							hasContent={!!message.content}
						/>
					{/if}
					{#if message.content}
						<div class="message-content">
							<Markdown source={message.content} onRefClick={handleRefClick}>
								{#if message.status === 'incomplete'}
									<div
										aria-label={t('chat.incompleteTooltip')}
										use:tooltip
										class="message-incomplete"
									>
										<StopIcon />
										<span>{t('chat.incomplete')}</span>
									</div>
								{/if}
							</Markdown>
							{#if message.role === 'user' && i === chatCtx.presentableMessages.length - 1 && !chatCtx.fetchable.fetching}
								<!-- only show retry if its the last message-->
								<button
									class="icon-button"
									use:tooltip
									aria-label={t('chat.retry')}
									onclick={() => chatCtx.retry()}><RetryIcon /></button
								>
							{/if}
						</div>
					{/if}

					{#if (message.status === 'completed' || message.status === 'incomplete') && (message.role !== 'assistant' || i > 0)}
						<div class="message-actions">
							{#if message.created}
								<span class="message-timestamp"
									>{message.created && getChatTimeStamp(message.created)}</span
								>
							{/if}
							{#if message.role === 'assistant'}
								<div class="buttons">
									{#if i === chatCtx.presentableMessages.length - 1}
										<!-- only show retry if its the last message-->
										<button
											class="icon-button"
											use:tooltip
											aria-label={t('chat.retry')}
											onclick={() => chatCtx.retry()}><RetryIcon /></button
										>
									{/if}
									<RoleGate minimumRole="operator">
										<button
											class="icon-button"
											use:tooltip
											aria-label={t('chat.debugInfo')}
											onclick={() => openMessageInfoDialog(message)}><InfoIcon /></button
										>
									</RoleGate>
								</div>
							{/if}
						</div>
					{/if}
				</div>
			{/each}

			{#if chatCtx.fetchable.fetching && chatCtx.latestAssistantMessage?.status !== 'in_progress' && chatCtx.latestAssistantMessage?.status !== 'completed'}
				<div class="message loading">
					<LoadingIndicator />
					{#if chatCtx.latestAssistantMessage}
						{#if chatCtx.latestAssistantMessage.status === 'reasoning'}
							<span>{t('chat.fetching.thinking')}</span>
						{:else if chatCtx.latestAssistantMessage.status === 'rag.tool_call' || chatCtx.latestAssistantMessage.status === 'rag.tool_result'}
							<span
								>{chatCtx.latestAssistantMessage.toolCalls?.[
									chatCtx.latestAssistantMessage.currentToolCallIndex ?? -1
								]?.description || t('chat.fetching.searchSources')}</span
							>
						{:else if chatCtx.latestAssistantMessage.status === 'rag.sources'}
							<span>{t('chat.fetching.sourcesFound')}</span>
						{/if}
					{/if}
				</div>
			{/if}

			{#if chatCtx.fetchable.error}
				<div class="error-message">
					{findErrorMessage(chatCtx.fetchable.error)}
					<div class="actions">
						<button class="glass" onclick={() => chatCtx.retry()}>Retry</button>
						<button class="icon-button" onclick={() => (chatCtx.fetchable.error = null)}
							><CloseIcon /></button
						>
					</div>
				</div>
			{/if}

			<div class="spacer" aria-hidden="true" style:height={chatSpacerHeight}></div>
		</div>
	</div>

	<ChatFooter />
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;
	.chat-dialog {
		flex: 1 1 auto;
		overflow: hidden;
		@include lib.flex-column();
		background: var(--surface-2);

		.messages-container {
			flex: 1;
			overflow-y: auto;
			overflow-y: overlay;
			position: relative;

			@include lib.flex-column();
			@include lib.flex-align();

			.messages-list {
				position: relative;
				width: 48rem;
				max-width: 100%;
				@include lib.flex-column();

				padding: 1rem;
				gap: 0.5rem;
				.message {
					font-size: 1rem;
					line-height: 1.5rem;
					@include lib.flex-column();
					.message-content {
						padding: 0.5rem 0;
						@include lib.flex-row();
						@include lib.flex-align();
						gap: 0.5rem;
						button.icon-button {
							margin: -0.5rem;
						}
					}
					&.user {
						max-width: 80%;
						align-self: flex-end;
						@include lib.flex-align(flex-start, flex-end);
						.message-content {
							border-radius: 1rem;
							padding: 0.5rem 1rem;
							background-color: var(--surface-3);
							color: var(--text-primary);
						}
						.message-actions {
							padding-right: 0.5rem;
						}
					}
					&.assistant {
						align-self: flex-start;
						background-color: transparent;
						color: var(--text-primary);
						align-self: stretch;
					}
					&.loading {
						padding: 1rem 0;
						@include lib.flex-row();
						@include lib.flex-align();
						gap: 1rem;

						@keyframes text-flow {
							0% {
								background-position: 100% 0;
							}

							to {
								background-position: 0% 0;
							}
						}
						span {
							color: transparent;
							background: linear-gradient(
								90deg,
								var(--text-tertiary),
								var(--text-secondary),
								var(--text-tertiary),
								var(--text-secondary),
								var(--text-tertiary)
							);
							background-repeat: no-repeat;
							-webkit-background-clip: text;
							background-clip: text;
							background-size: 200% 100%;
							animation: text-flow 1s linear infinite;
						}
					}
					.message-incomplete {
						@include lib.flex-row();
						@include lib.flex-align(flex-start, center);
						gap: 0.25rem;
						padding: 0 0.5rem 0 0.25rem;
						border-radius: 0.5rem;
						background-color: var(--surface-warning);
						color: var(--text-warning, var(--text-secondary));
						font-size: 0.75rem;
						justify-self: flex-start;
						align-self: flex-start;
					}

					.message-actions {
						opacity: 0;
						transition: opacity 200ms ease-out;
						@include lib.flex-row();
						gap: 1rem;
						@include lib.flex-align(flex-start, center);
						button {
							color: var(--text-secondary);
						}
						.message-timestamp {
							justify-self: flex-start;
							color: var(--text-tertiary);
							font-size: 0.75rem;
						}
						.buttons {
							@include lib.flex-row();
							gap: 0.5rem;
						}
					}
					&:hover {
						.message-actions {
							opacity: 1;
						}
					}
				}
			}
		}

		.error-message {
			color: var(--text-error);
			background-color: var(--surface-error);
			padding: 1rem;
			border-radius: 1rem;
			margin: 0.5rem 0;
			@include lib.flex-row();
			@include lib.flex-align(space-between, center);
			white-space: pre-line;
			.actions {
				@include lib.flex-row();
				gap: 0.5rem;
				button {
					flex: 0 0 auto;
				}
			}
		}
	}
</style>
